/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useRef, useState } from 'react';

import { Autocomplete, Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon } from '@coursera/cds-icons';

import timezones from 'bundles/common/constants/timezones';

import _t from 'i18n!nls/account-settings';

const styles = {
  label: css`
    && {
      margin: 0;
    }
  `,
  autocomplete: css`
    margin-top: var(--cds-spacing-50);
    display: flex;

    .cds-Search-prefix,
    .cds-SearchClearButton-searchClearButton {
      display: none;
    }

    .cds-Search-suffix {
      padding-right: var(--cds-spacing-150);
    }
  `,
  icon: css`
    display: flex;
    cursor: pointer;
  `,
};

type Props = {
  value: string;
  onChange: (value: string) => void;
};

const TimezoneSelect = ({ value, onChange }: Props) => {
  const groups = timezones();
  const labelId = 'settings-basic-timezone-label';
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const handleOpen = useCallback<() => void>(() => {
    setOpen(true);
    ref.current?.focus();
  }, []);

  const handleClose = useCallback<() => void>(() => {
    setOpen(false);
  }, []);

  return (
    <div>
      <Typography2 id={labelId} variant="subtitleMedium" component="label" css={styles.label}>
        {_t('Timezone')}
      </Typography2>
      <Autocomplete
        ref={ref}
        css={styles.autocomplete}
        id="settings-basic-timezone"
        aria-label={_t('Timezone')}
        placeholder={_t('Search for a timezone')}
        popoverTrigger="focus"
        selectedKey={value || undefined}
        onSelectionChange={(key) => {
          if (key !== null) {
            onChange(String(key));
          }
        }}
        onOpen={handleOpen}
        onClose={handleClose}
        inputActionButtons={
          <div css={styles.icon}>
            {open ? (
              <ChevronUpIcon size="large" onClick={handleClose} />
            ) : (
              <ChevronDownIcon size="large" onClick={handleOpen} />
            )}
          </div>
        }
        fullWidth
      >
        {Object.values(groups).map((group) => (
          <Autocomplete.Group key={group.groupName} title={group.groupName} aria-label={group.groupName}>
            {Object.entries(group.zones)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([zoneName, zoneValue]) => (
                <Autocomplete.Option key={zoneValue} textValue={zoneName} aria-label={`${_t('Timezone')}: ${zoneName}`}>
                  {zoneName}
                </Autocomplete.Option>
              ))}
          </Autocomplete.Group>
        ))}
      </Autocomplete>
    </div>
  );
};

export default TimezoneSelect;
